import { useState, useEffect } from 'preact/hooks'
import { X, ArrowLeft, ArrowRight, User, Users } from 'lucide-preact'
import { useAuth } from '../../auth/useAuth'
import { userRolesApi } from '../../api/userRoles'
import type { ManagingCompanyUsersDto } from '../../api/userRoles'
import { RoleSelector } from '../common/RoleSelector'
import { ManagingCompanyPicker } from './ManagingCompanyPicker'
import { useToast } from '../../hooks/useToast'
import type {
  UserRoleAssignmentDto,
  UserProfileWithRolesDto,
} from '../../types/userRoles'
import { UserProfileCreationStatus } from '../../types/userRoles'
import type { ClientCompanyViewDto, ClientWorkspaceViewDto } from '../../types/company'
import { isValidEmail } from '../../utils/validation'
import { getDisplayErrorMessages, parseValidationErrors } from '../../utils/errorHandling'

interface UserCreateModalProps {
  isOpen: boolean
  selectedCompany: ClientCompanyViewDto
  onClose: () => void
  onSuccess: (newUser: UserProfileWithRolesDto) => void
  /**
   * ORB-1019: people the multi-select picker just added. The modal stays open, because each row reports
   * its own result and a failed row stays ticked for another try.
   */
  onUsersAdded: (newUsers: UserProfileWithRolesDto[]) => void
}

type Step = 'details' | 'roles'

/**
 * The two ways into a managed company: grant somebody who already signs in at the managing company,
 * or invite somebody who works here. Alternatives, never both on screen — a picker with a form under
 * it is what let ORB-709's mismatched person through.
 */
type CreateMode = 'existing' | 'invite'

interface UserDetails {
  firstName: string
  lastName: string
  email: string
}

/** A refusal of the typed address, whether we caught it or the server did. */
interface EmailRefusal {
  /** Server prose, rendered as it arrives. Empty when the refusal is the managing-domain one below. */
  messages: string[]
  /** Set only for the managing company's own domain, which has its own way in and needs no address. */
  pointsAtPicker: boolean
}

const domainOf = (email: string) => email.trim().toLowerCase().split('@')[1] ?? ''

export function UserCreateModal({
  isOpen,
  selectedCompany,
  onClose,
  onSuccess,
  onUsersAdded
}: UserCreateModalProps) {
  const { userProfile, allWorkspaces } = useAuth()
  const { showSuccess, showWarning } = useToast()
  const [currentStep, setCurrentStep] = useState<Step>('details')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [collapsedWorkspaces, setCollapsedWorkspaces] = useState<Set<string>>(new Set())

  // Get workspaces from auth state instead of API
  const [companyWorkspaces, setCompanyWorkspaces] = useState<Pick<ClientWorkspaceViewDto, 'Id' | 'Name'>[]>([])
  const [fetchedCompanyData, setFetchedCompanyData] = useState<ClientCompanyViewDto | null>(null)

  // Step 1: User details
  const [userDetails, setUserDetails] = useState<UserDetails>({
    firstName: '',
    lastName: '',
    email: ''
  })
  const [detailsErrors, setDetailsErrors] = useState<Record<string, string>>({})
  const [emailRefusal, setEmailRefusal] = useState<EmailRefusal | null>(null)

  // Step 1, managed companies only: who is above this company, and who works there
  const [mode, setMode] = useState<CreateMode>('invite')
  const [managingCompanyRoster, setManagingCompanyRoster] = useState<ManagingCompanyUsersDto | null>(null)
  const [managingCompanyUsersLoading, setManagingCompanyUsersLoading] = useState(false)
  // Remounts the picker, so its ticks and results go when the operator switches route and back.
  const [pickerKey, setPickerKey] = useState(0)

  // Step 2: Role assignment
  const [companyRoles, setCompanyRoles] = useState<string[]>([])
  const [workspaceRoles, setWorkspaceRoles] = useState<Record<string, string[]>>({})
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('details')
      setUserDetails({ firstName: '', lastName: '', email: '' })
      setDetailsErrors({})
      setEmailRefusal(null)
      // ManagedByCompanyId is on the prop, so the route is known before either call below lands and
      // today's form never flashes up in a managed company only to be replaced by the picker.
      setMode(selectedCompany.ManagedByCompanyId ? 'existing' : 'invite')
      setManagingCompanyRoster(null)
      setPickerKey(key => key + 1)
      setCompanyRoles([])
      setWorkspaceRoles({})
      setSelectedWorkspaces([])
      setError(null)
      setCollapsedWorkspaces(new Set())
      setFetchedCompanyData(null)
      // Load workspaces from auth state
      const workspaces = (allWorkspaces || []).filter(w => w.ClientCompanyId === selectedCompany.Id)
      setCompanyWorkspaces(workspaces)
      setFetchedCompanyData(selectedCompany)
    }
  }, [isOpen, selectedCompany.Id, allWorkspaces])

  /**
   * ORB-709: both the managing company's name and its roster hang off this company's
   * ManagedByCompanyId, so a second managing company reads its own name and its own people with no
   * change here. If either call fails the screen stays on today's invite-only form rather than
   * offering a picker it cannot fill.
   */
  useEffect(() => {
    const managedByCompanyId = selectedCompany.ManagedByCompanyId
    if (!isOpen || !managedByCompanyId) return

    let cancelled = false
    setManagingCompanyUsersLoading(true)

    // One call. The managing company's name and domains travel with the roster because an
    // administrator at this company can read the roster but not necessarily the managing company
    // itself, so fetching the name separately would fail for exactly the people this screen is for.
    userRolesApi.getManagingCompanyUsers(selectedCompany.Id)
      .then(managingCompanyUsersResponse => {
        if (cancelled) return
        setManagingCompanyRoster(managingCompanyUsersResponse.data ?? null)
      })
      .catch(() => {
        // Nothing to say: without a managing company the screen is today's form, which still works.
      })
      .finally(() => {
        if (!cancelled) setManagingCompanyUsersLoading(false)
      })

    return () => { cancelled = true }
  }, [isOpen, selectedCompany.Id, selectedCompany.ManagedByCompanyId])

  // A managing company we could not name is one we cannot offer — but one still being read is, or the
  // operator would be handed the invite form for the moment the two calls take.
  const managingCompanyName = managingCompanyRoster?.ManagingCompanyName ?? null
  const managingCompanyPeople = managingCompanyRoster?.Users ?? []

  const isManagedCompany = managingCompanyName !== null
    || (Boolean(selectedCompany.ManagedByCompanyId) && managingCompanyUsersLoading)
  const activeMode: CreateMode = isManagedCompany ? mode : 'invite'

  /** Switching mode drops the other mode's answer: only one of them can be what the operator meant. */
  const handleModeChange = (nextMode: CreateMode) => {
    setMode(nextMode)
    setPickerKey(key => key + 1)
    setUserDetails({ firstName: '', lastName: '', email: '' })
    setDetailsErrors({})
    setEmailRefusal(null)
    setError(null)
  }

  // Validate user details (Step 1)
  const validateUserDetails = (): boolean => {
    const errors: Record<string, string> = {}

    if (!userDetails.firstName.trim()) {
      errors.firstName = 'First name is required'
    }

    if (!userDetails.lastName.trim()) {
      errors.lastName = 'Last name is required'
    }

    if (!userDetails.email.trim()) {
      errors.email = 'Email is required'
    } else if (!isValidEmail(userDetails.email)) {
      errors.email = 'Please enter a valid email address'
    }

    // An address on any domain the managing company holds belongs on the other card, so it is refused
    // here rather than after a round trip whose only possible answer is no. Every domain, not just the
    // canonical one: OrbitalX holds both orbitalx.com and orbitalx.io, and the address that caused the
    // incident was on the second of them.
    const managingCompanyDomains = (managingCompanyRoster?.ManagingCompanyDomains ?? [])
      .map(domain => domain.trim().toLowerCase())
      .filter(Boolean)
    const belongsToManagingCompany = managingCompanyDomains.includes(domainOf(userDetails.email))
    setEmailRefusal(belongsToManagingCompany ? { messages: [], pointsAtPicker: true } : null)

    setDetailsErrors(errors)
    return Object.keys(errors).length === 0 && !belongsToManagingCompany
  }

  // Validate role assignments (Step 2)
  const validateRoleAssignments = (): boolean => {
    const hasCompanyRoles = companyRoles.length > 0
    const hasWorkspaceRoles = selectedWorkspaces.some(wsId =>
      (workspaceRoles[wsId] || []).length > 0
    )

    // User must have at least one role at either company OR workspace level
    // Company roles can exist without workspace roles (and vice versa)
    if (!hasCompanyRoles && !hasWorkspaceRoles) {
      setError('User must have at least one role assigned at either company or workspace level')
      return false
    }

    setError(null)
    return true
  }

  const handleDetailsNext = () => {
    if (validateUserDetails()) {
      setCurrentStep('roles')
    }
  }

  const handleRolesBack = () => {
    setCurrentStep('details')
    setError(null)
  }

  const handleCompanyRoleChange = (role: string, checked: boolean) => {
    // Single selection: set to just this role, or clear if deselecting
    setCompanyRoles(checked ? [role] : [])
    setError(null)
  }

  const handleWorkspaceRoleChange = (workspaceId: string, role: string, checked: boolean) => {
    setWorkspaceRoles(prev => {
      const newRoles = {
        ...prev,
        // Single selection per workspace: set to just this role, or clear if deselecting
        [workspaceId]: checked ? [role] : []
      }

      // Auto-update selectedWorkspaces based on which workspaces have roles
      const workspacesWithRoles = Object.keys(newRoles).filter(wsId =>
        (newRoles[wsId] || []).length > 0
      )
      setSelectedWorkspaces(workspacesWithRoles)

      return newRoles
    })
    setError(null)
  }

  const toggleWorkspaceCollapse = (workspaceId: string) => {
    setCollapsedWorkspaces(prev => {
      const newSet = new Set(prev)
      if (newSet.has(workspaceId)) {
        newSet.delete(workspaceId)
      } else {
        newSet.add(workspaceId)
      }
      return newSet
    })
  }

  const handleCreateUser = async () => {
    if (!validateRoleAssignments()) {
      return
    }

    // Use fetchedCompanyData if available, otherwise fall back to selectedCompany
    const companyToUse = fetchedCompanyData || selectedCompany

    if (!userProfile?.ClientCompanyId) {
      setError('Missing required company information.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Build workspace roles array only for workspaces that have roles assigned
      const workspacesWithRoles = Object.keys(workspaceRoles).filter(wsId =>
        (workspaceRoles[wsId] || []).length > 0
      )

      const workspaceRolesList = workspacesWithRoles.map(workspaceId => ({
        WorkspaceId: workspaceId,
        Roles: workspaceRoles[workspaceId] || []
      }))

      const roleAssignment: UserRoleAssignmentDto = {
        CompanyRoles: companyRoles,
        WorkspaceRoles: workspaceRolesList
      }

      // Only the typing route reaches here: the picker grants by id through its own batch call, so an
      // address typed here always creates somebody new and is checked against this company's domains.
      const response = await userRolesApi.createUser(companyToUse.Id, {
        ClientCompanyId: companyToUse.Id,
        FirstName: userDetails.firstName.trim(),
        LastName: userDetails.lastName.trim(),
        Email: userDetails.email.trim(),
        InitialRoles: roleAssignment
      })

      if (response.error) {
        // A refusal of the address has to land under the address. This used to return early and leave
        // the message as a seven-second toast over a form that looked untouched, so the operator was
        // told nothing they could act on.
        if (response.status === 422) {
          const messages = getDisplayErrorMessages(response.error, 'That user could not be added.')

          // Only an address refusal belongs under the address. Role validation runs before the email
          // checks server-side, so an unfiltered rewind drags a roles message onto the Email field of a
          // step the operator already cleared, with their role choices still set but off screen.
          const addressRefusals = parseValidationErrors(response).filter(
            validationError => validationError.FieldName === 'Email'
          )

          if (addressRefusals.length > 0) {
            setEmailRefusal({ messages: addressRefusals.map(refusal => refusal.Message), pointsAtPicker: false })
            setCurrentStep('details')
          } else {
            setError(messages.join(' '))
          }
          return
        }
        // For other errors, show in modal
        setError(response.error)
        return
      }

      if (response.data) {
        const { Status, UserProfileWithRoles, InviteEmail } = response.data

        // The user exists either way — the invitation email is a separate outcome, and used to be reported
        // as a success no matter what became of it.
        if (InviteEmail && !InviteEmail.Sent) {
          showWarning(
            'Invitation email not sent',
            `${InviteEmail.Reason ?? 'The invitation email could not be sent.'} The user was added and can be re-invited.`,
            10000
          )
        } else {
          switch (Status) {
            case UserProfileCreationStatus.Created:
              showSuccess('User Invited', 'User invited successfully')
              break
            case UserProfileCreationStatus.ExistingUserAdded:
              showSuccess('User Added', 'User from managing company added to this company')
              break
            case UserProfileCreationStatus.Reactivated:
              showSuccess('User Reactivated', 'User reactivated and re-invited')
              break
          }
        }

        onSuccess(UserProfileWithRoles)
        onClose()
      } else {
        setError('User created but no data returned')
      }
    } catch {
      setError('Failed to create user. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Both labels name companies resolved at runtime, so a second managing company reads its own name.
  const modeOptions: { mode: CreateMode; title: string; subtitle: string }[] = managingCompanyName
    ? [
      {
        mode: 'existing',
        title: `Give someone from ${managingCompanyName} access`,
        subtitle: 'They already have a sign-in'
      },
      {
        mode: 'invite',
        title: `Add someone who works at ${selectedCompany.Name}`,
        subtitle: 'They will be invited by email'
      }
    ]
    : []

  const modeSwitcher = modeOptions.length > 0 && (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {modeOptions.map(option => (
        <button
          key={option.mode}
          type="button"
          aria-pressed={activeMode === option.mode}
          onClick={() => handleModeChange(option.mode)}
          className={`text-left px-4 py-3 rounded-lg border transition-colors ${activeMode === option.mode
            ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20 text-slate-900 dark:text-white'
            : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
        >
          <span className="block text-sm font-medium">{option.title}</span>
          <span className="block mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {option.subtitle}
          </span>
        </button>
      ))}
    </div>
  )

  // The picker chooses roles on each row, so it is one step and shows no Details/Roles stepper.
  const isPicking = activeMode === 'existing'

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 max-w-full flex">
        <div className="w-screen max-w-4xl">
          <div className="h-full flex flex-col bg-white dark:bg-slate-900 shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-slate-900 dark:text-white">
                      {isPicking ? `Add users to ${selectedCompany.Name}` : 'Add New User'}
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {isPicking
                        ? `Pick people from ${managingCompanyName ?? 'your team'}`
                        : currentStep === 'details' ? 'Step 1: User Information' : 'Step 2: Role Assignment'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Progress indicator */}
              {!isPicking && (
              <div className="mt-4">
                <div className="flex items-center">
                  <div className={`flex items-center ${currentStep === 'details' ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'}`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-medium ${currentStep === 'details'
                        ? 'border-primary-600 dark:border-primary-400 bg-primary-600 dark:bg-primary-400 text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-400'
                      }`}>
                      1
                    </div>
                    <span className="ml-2 text-sm font-medium">Details</span>
                  </div>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 mx-4" />
                  <div className={`flex items-center ${currentStep === 'roles' ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'}`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-medium ${currentStep === 'roles'
                        ? 'border-primary-600 dark:border-primary-400 bg-primary-600 dark:bg-primary-400 text-white'
                        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-400'
                      }`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium">Roles</span>
                  </div>
                </div>
              </div>
              )}
            </div>

            {isPicking ? (
              <ManagingCompanyPicker
                key={pickerKey}
                companyId={selectedCompany.Id}
                companyName={selectedCompany.Name}
                managingCompanyName={managingCompanyName ?? ''}
                people={managingCompanyPeople}
                loading={managingCompanyUsersLoading}
                modeSwitcher={modeSwitcher}
                onClose={onClose}
                onAdded={onUsersAdded}
              />
            ) : (
            <>
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-6">
                {currentStep === 'details' ? (
                  /* Step 1: User Details */
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-4">
                        User Information
                      </h3>
                      {!isManagedCompany && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                          Enter the basic information for the new user. An invitation email will be sent to the provided email address.
                        </p>
                      )}
                    </div>

                    {modeSwitcher}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          value={userDetails.firstName}
                          onChange={(e) => setUserDetails(prev => ({
                            ...prev,
                            firstName: (e.target as HTMLInputElement).value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${detailsErrors.firstName
                              ? 'border-red-300 dark:border-red-600'
                              : 'border-slate-300 dark:border-slate-600'
                            } bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100`}
                          placeholder="Enter first name"
                        />
                        {detailsErrors.firstName && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {detailsErrors.firstName}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          value={userDetails.lastName}
                          onChange={(e) => setUserDetails(prev => ({
                            ...prev,
                            lastName: (e.target as HTMLInputElement).value
                          }))}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${detailsErrors.lastName
                              ? 'border-red-300 dark:border-red-600'
                              : 'border-slate-300 dark:border-slate-600'
                            } bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100`}
                          placeholder="Enter last name"
                        />
                        {detailsErrors.lastName && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {detailsErrors.lastName}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={userDetails.email}
                        onChange={(e) => {
                          setUserDetails(prev => ({
                            ...prev,
                            email: (e.target as HTMLInputElement).value
                          }))
                          setEmailRefusal(null)
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${detailsErrors.email
                            ? 'border-red-300 dark:border-red-600'
                            : 'border-slate-300 dark:border-slate-600'
                          } bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100`}
                        placeholder="Enter email address"
                      />
                      {detailsErrors.email && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {detailsErrors.email}
                        </p>
                      )}
                      {emailRefusal && (
                        <div className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {emailRefusal.pointsAtPicker && managingCompanyName ? (
                            <p>
                              That is one of your own team. Choose{' '}
                              <button
                                type="button"
                                onClick={() => handleModeChange('existing')}
                                className="font-medium underline underline-offset-2 text-red-600 dark:text-red-400"
                              >
                                Someone from {managingCompanyName}
                              </button>{' '}
                              to give them access to {selectedCompany.Name}.
                            </p>
                          ) : (
                            emailRefusal.messages.map(message => <p key={message}>{message}</p>)
                          )}
                        </div>
                      )}
                      {!detailsErrors.email && !emailRefusal && (
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          An invitation email will be sent to this address
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Step 2: Role Assignment */
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                        Role Assignment
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                        Assign roles to <span className="font-medium">{`${userDetails.firstName} ${userDetails.lastName}`}</span>.
                        At least one role must be assigned at either company or workspace level.
                      </p>
                    </div>

                    <RoleSelector
                      companyRoles={companyRoles}
                      workspaceRoles={workspaceRoles}
                      selectedWorkspaces={selectedWorkspaces}
                      companyWorkspaces={companyWorkspaces}
                      onCompanyRoleChange={handleCompanyRoleChange}
                      onWorkspaceRoleChange={handleWorkspaceRoleChange}
                      collapsedWorkspaces={collapsedWorkspaces}
                      onToggleWorkspaceCollapse={toggleWorkspaceCollapse}
                      showEffectiveRoles={true}
                    />

                    {error && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  {currentStep === 'roles' && (
                    <button
                      onClick={handleRolesBack}
                      disabled={loading}
                      className="flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Back</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="px-4 py-2 text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>

                  {currentStep === 'details' ? (
                    <button
                      onClick={handleDetailsNext}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>Next</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleCreateUser}
                      disabled={loading}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Users className="w-4 h-4" />
                      <span>{loading ? 'Creating...' : 'Create User'}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
